const STORAGE_KEY = 'fpVehicleSelection';
const COVER_ORDER = ['high', 'low', 'driver', 'single-passenger', 'passenger', 'double-passenger'];

/** @type {Promise<Record<string, {brands: NavBrand[]}>> | null} */
let navIndex = null;

/**
 * The menu's brands and models, built from the live products by
 * scripts/build_filter_index.py (assets/fp-nav-index.json). Fetched once per
 * page, on the first tab that opens, and shared by the header and the drawer.
 * @typedef {{l: string, c: Record<string, string>}} NavModel  label, cover -> product handle
 * @typedef {{brand: string, models: NavModel[]}} NavBrand
 * @param {string} url
 */
function loadNavIndex(url) {
  if (!navIndex) {
    navIndex = fetch(url)
      .then((response) => (response.ok ? response.json() : {}))
      .catch(() => {
        navIndex = null;
        return {};
      });
  }
  return navIndex;
}

/**
 * The header's shop menu.
 * Desktop: the header's Shop button ([data-fp-shop-trigger], in
 * blocks/_header-menu.liquid) opens one mega-menu: Safety kit and the
 * vehicles in a rail under sector headings; the vehicle on show lists its
 * brands, and the brand under the pointer its models, each linking to its
 * cover. Drawer: machine types drill down to brand -> model ->
 * cover, and model and cover links open the exact product (see
 * assets/fp-nav-index.json).
 */
class FpCatalogNav extends HTMLElement {
  #closeTimer = 0;
  #openTimer = 0;
  /** Opened by hovering the Shop button, so leaving it closes it again. */
  #openedByHover = false;

  connectedCallback() {
    // Nothing is built here. The brand and model lists run to several hundred
    // links, and building them at upgrade put a third of a second of
    // scripting in front of the first tap -- on a phone, for a menu that is
    // behind the drawer button and may never be opened. Each list now builds
    // itself the first time it opens; see `#fillPanel` and `#buildTab`.
    if (this.dataset.mode === 'drawer') {
      this.addEventListener('click', this.#onDrawerClick, true);
      return;
    }
    // The Shop button lives in the header menu, outside this element, so the
    // menu listens on the document and works out whether an event is its own.
    document.addEventListener('click', this.#onMegaClick);
    document.addEventListener('keydown', this.#onMegaKeydown);
    document.addEventListener('focusin', this.#onMegaFocusIn);
    document.addEventListener('pointerover', this.#onMegaPointerOver);
    document.addEventListener('pointerout', this.#onMegaPointerOut);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onDrawerClick, true);
    document.removeEventListener('click', this.#onMegaClick);
    document.removeEventListener('keydown', this.#onMegaKeydown);
    document.removeEventListener('focusin', this.#onMegaFocusIn);
    document.removeEventListener('pointerover', this.#onMegaPointerOver);
    document.removeEventListener('pointerout', this.#onMegaPointerOut);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  /* ---- Desktop mega-menu ------------------------------------------------ */

  get #mega() {
    return this.querySelector('.fp-mega');
  }

  get #isOpen() {
    const mega = this.#mega;
    return mega instanceof HTMLElement && !mega.hidden;
  }

  /** The Shop button on show (the header has one; the drawer has none). */
  get #trigger() {
    const triggers = [...document.querySelectorAll('[data-fp-shop-trigger]')];
    const visible = triggers.find((node) => node instanceof HTMLElement && node.offsetParent !== null);
    return visible instanceof HTMLElement ? visible : null;
  }

  /**
   * The Shop button or the menu itself.
   * @param {EventTarget | null} node
   */
  #inMenu(node) {
    const element = node instanceof Element ? node : node instanceof Node ? node.parentElement : null;
    return Boolean(element && (this.contains(element) || element.closest('[data-fp-shop-trigger]')));
  }

  /**
   * Open the menu on a category (the last one shown, or the first), or switch
   * to it while open.
   * @param {string} [category]
   */
  #openMega(category) {
    const mega = this.#mega;
    if (!(mega instanceof HTMLElement)) return;
    window.clearTimeout(this.#closeTimer);
    const firstCategory = mega.querySelector('.fp-mega__rail-button')?.getAttribute('data-category') || '';
    const next = category || mega.dataset.category || firstCategory;
    if (!next) return;
    const wasOpen = !mega.hidden;
    mega.dataset.category = next;
    mega.hidden = false;

    document.querySelectorAll('[data-fp-shop-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });
    mega.querySelectorAll('.fp-mega__rail-button').forEach((button) => {
      button.setAttribute('aria-expanded', String(button.getAttribute('data-category') === next));
    });
    mega.querySelectorAll('.fp-mega__panel').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const active = panel.dataset.category === next;
      panel.hidden = !active;
      if (active) this.#fillPanel(panel);
    });

    if (!wasOpen) this.#fitMega(mega);
  }

  /** @param {{ returnFocus?: boolean }} [options] */
  #closeMega({ returnFocus = false } = {}) {
    const mega = this.#mega;
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    if (!(mega instanceof HTMLElement) || mega.hidden) return;
    mega.hidden = true;
    this.#openedByHover = false;
    document.querySelectorAll('[data-fp-shop-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    mega.querySelectorAll('.fp-mega__rail-button').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (returnFocus) this.#trigger?.focus();
  }

  /** The rail button for the category on show. */
  #activeRailButton() {
    const category = this.#mega?.getAttribute('data-category') || '';
    const button = this.querySelector(`.fp-mega__rail-button[data-category="${CSS.escape(category)}"]`);
    return button instanceof HTMLElement ? button : null;
  }

  /** Keep the menu inside the viewport under the tab row; the columns scroll. @param {HTMLElement} mega */
  #fitMega(mega) {
    const top = this.getBoundingClientRect().bottom;
    mega.style.setProperty('--fp-mega-max-height', `${Math.max(280, window.innerHeight - top - 16)}px`);
  }

  /**
   * Fill a vehicle's panel, once: its brands down the left (alphabetical),
   * and the first brand's models beside them.
   * @param {HTMLElement} panel
   */
  async #fillPanel(panel) {
    if (panel.dataset.fpBuilt) return;
    const index = await loadNavIndex(this.dataset.index || '');
    if (panel.dataset.fpBuilt) return;
    panel.dataset.fpBuilt = '1';

    const catalog = panel.dataset.catalog || '';
    const collectionUrl = panel.dataset.collectionUrl || '';
    const brands = [...(index[catalog]?.brands || [])].sort((a, b) =>
      a.brand.localeCompare(b.brand, 'en-GB', { sensitivity: 'base' })
    );
    this.#panelBrands.set(panel, brands);

    const list = panel.querySelector('.fp-mega__brands');
    if (!(list instanceof HTMLElement)) return;
    list.replaceChildren(
      ...brands.map((entry) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'fp-mega__link fp-mega__brand';
        link.href = this.#brandHref(collectionUrl, entry.brand);
        link.dataset.brand = entry.brand;
        link.textContent = entry.brand;
        link.append(this.#chevron());
        item.append(link);
        return item;
      })
    );
    if (brands.length) this.#showBrand(panel, brands[0].brand);
  }

  /** The brand lists each panel was built from. @type {WeakMap<HTMLElement, NavBrand[]>} */
  #panelBrands = new WeakMap();

  /**
   * In a group's panel (Harvesters), show one of its types: that type's
   * brands, then the first brand's models.
   * @param {HTMLElement} panel
   * @param {HTMLElement} type
   */
  #showType(panel, type) {
    const catalog = type.dataset.type || '';
    if (!catalog || (panel.dataset.catalog === catalog && panel.dataset.fpBuilt)) return;
    panel.querySelectorAll('.fp-mega__type').forEach((link) => link.classList.toggle('is-active', link === type));
    panel.dataset.catalog = catalog;
    panel.dataset.collectionUrl = type.dataset.typeUrl || '';
    delete panel.dataset.fpBuilt;
    delete panel.dataset.brand;
    this.#fillPanel(panel);
  }

  /**
   * Show one brand's models in its vehicle's panel: a heading with a link to
   * all of the brand's covers, then the models in columns. A model with more
   * than one cover (high and low back, driver and passenger) lists them after
   * its name.
   * @param {HTMLElement} panel
   * @param {string} brand
   */
  #showBrand(panel, brand) {
    if (panel.dataset.brand === brand) return;
    const entry = (this.#panelBrands.get(panel) || []).find((item) => item.brand === brand);
    const area = panel.querySelector('.fp-mega__models');
    if (!entry || !(area instanceof HTMLElement)) return;
    panel.dataset.brand = brand;

    panel.querySelectorAll('.fp-mega__brand').forEach((link) => {
      link.classList.toggle('is-active', link instanceof HTMLElement && link.dataset.brand === brand);
    });

    const catalog = panel.dataset.catalog || '';
    const head = document.createElement('div');
    head.className = 'fp-mega__models-head';
    const title = document.createElement('p');
    title.className = 'fp-mega__label';
    title.textContent = `${brand} models`;
    const all = document.createElement('a');
    all.className = 'fp-mega__brand-all';
    all.href = this.#brandHref(panel.dataset.collectionUrl || '', brand);
    all.textContent = `All ${brand} covers`;
    head.append(title, all);

    const list = document.createElement('ul');
    list.className = 'fp-mega__model-list';
    list.setAttribute('role', 'list');
    list.dataset.brand = brand;
    // Models come from the index already in natural order, newest
    // generation first within a name (scripts/build_filter_index.py).
    (entry.models || []).forEach((model) => {
      const covers = this.#coversFor(catalog, model);
      if (!covers.length) return;
      const item = document.createElement('li');
      item.className = 'fp-mega__model';
      const link = document.createElement('a');
      link.className = 'fp-mega__link fp-mega__model-link';
      link.href = this.#productHref(model, covers[0]);
      link.dataset.model = model.l;
      link.textContent = model.l;
      item.append(link);
      if (covers.length > 1) {
        const fits = document.createElement('span');
        fits.className = 'fp-mega__fits';
        covers.forEach((cover) => {
          const fit = document.createElement('a');
          fit.className = 'fp-mega__link fp-mega__fit';
          fit.href = this.#productHref(model, cover);
          fit.dataset.model = model.l;
          fit.textContent = this.#labelForCover(cover).replace(/ cover$/, '').replace('-back', ' back');
          fits.append(fit);
        });
        item.append(fits);
      }
      list.append(item);
    });

    area.replaceChildren(head, list);
    area.scrollTop = 0;
  }

  /** @param {MouseEvent} event */
  #onMegaClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.#inMenu(target)) return;

    const trigger = target.closest('[data-fp-shop-trigger]');
    if (trigger instanceof HTMLElement) {
      window.clearTimeout(this.#openTimer);
      // A click on a menu that hover opened keeps it open (and stops leaving
      // closing it); a click on a menu that was already clicked open shuts it.
      if (this.#isOpen && !this.#openedByHover) {
        this.#closeMega();
        return;
      }
      this.#openedByHover = false;
      this.#openMega();
      // Keyboard activation (Enter / Space reports detail 0) moves focus into
      // the menu, which sits under the header rather than next to the button.
      if (event.detail === 0) this.#activeRailButton()?.focus();
      return;
    }

    // Anything clicked inside the menu means someone is using it: keep it.
    this.#openedByHover = false;

    if (target.closest('.fp-mega__close')) {
      this.#closeMega({ returnFocus: true });
      return;
    }

    const rail = target.closest('.fp-mega__rail-button');
    if (rail instanceof HTMLElement) this.#openMega(rail.dataset.category || '');
  };

  /** @param {KeyboardEvent} event */
  #onMegaKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.#inMenu(target)) return;

    if (event.key === 'Escape' && this.#isOpen) {
      event.preventDefault();
      this.#closeMega({ returnFocus: true });
      return;
    }

    if (target.matches('[data-fp-shop-trigger]') && event.key === 'ArrowDown') {
      event.preventDefault();
      this.#openedByHover = false;
      this.#openMega();
      this.#activeRailButton()?.focus();
      return;
    }

    if (target.matches('.fp-mega__rail-button')) {
      const buttons = [...this.querySelectorAll('.fp-mega__rail-button')];
      const at = buttons.indexOf(target);
      let next = null;
      if (event.key === 'ArrowDown') next = buttons[(at + 1) % buttons.length];
      else if (event.key === 'ArrowUp') next = buttons[(at - 1 + buttons.length) % buttons.length];
      else if (event.key === 'Home') next = buttons[0];
      else if (event.key === 'End') next = buttons[buttons.length - 1];
      else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const panel = this.querySelector(`#${CSS.escape(target.getAttribute('aria-controls') || '')}`);
        const first = panel?.querySelector('a[href]');
        if (first instanceof HTMLElement) first.focus();
        return;
      }
      if (next instanceof HTMLElement) {
        event.preventDefault();
        next.focus();
        this.#openMega(next.dataset.category || '');
      }
      return;
    }

    if (event.key === 'ArrowLeft' && target.closest('.fp-mega__panel')) {
      event.preventDefault();
      this.#activeRailButton()?.focus();
    }
  };

  /** Close once focus moves somewhere outside the button and the menu. @param {FocusEvent} event */
  #onMegaFocusIn = (event) => {
    if (this.#isOpen && !this.#inMenu(event.target)) {
      this.#closeMega();
      return;
    }
    const type = event.target instanceof Element ? event.target.closest('.fp-mega__type') : null;
    const typePanel = type?.closest('.fp-mega__panel');
    if (type instanceof HTMLElement && typePanel instanceof HTMLElement) {
      this.#showType(typePanel, type);
      return;
    }
    const brand = event.target instanceof Element ? event.target.closest('.fp-mega__brand') : null;
    const panel = brand?.closest('.fp-mega__panel');
    if (brand instanceof HTMLElement && panel instanceof HTMLElement) this.#showBrand(panel, brand.dataset.brand || '');
  };

  /** Hover opens the menu from the Shop button and switches rail categories. @param {PointerEvent} event */
  #onMegaPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !this.#inMenu(event.target)) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    window.clearTimeout(this.#closeTimer);

    // Start fetching the brands while the pointer is still on its way.
    loadNavIndex(this.dataset.index || '');

    const rail = target.closest('.fp-mega__rail-button');
    if (rail instanceof HTMLElement) {
      this.#openMega(rail.dataset.category || '');
      return;
    }

    const type = target.closest('.fp-mega__type');
    const typePanel = type?.closest('.fp-mega__panel');
    if (type instanceof HTMLElement && typePanel instanceof HTMLElement) {
      this.#showType(typePanel, type);
      return;
    }

    const brand = target.closest('.fp-mega__brand');
    const brandPanel = brand?.closest('.fp-mega__panel');
    if (brand instanceof HTMLElement && brandPanel instanceof HTMLElement) {
      this.#showBrand(brandPanel, brand.dataset.brand || '');
      return;
    }

    if (target.closest('[data-fp-shop-trigger]') && !this.#isOpen) {
      window.clearTimeout(this.#openTimer);
      // A short pause so sweeping across the header to the basket does not
      // flash the menu open.
      this.#openTimer = window.setTimeout(() => {
        if (this.#isOpen) return;
        this.#openedByHover = true;
        this.#openMega();
      }, 120);
    }
  };

  /** @param {PointerEvent} event */
  #onMegaPointerOut = (event) => {
    if (event.pointerType !== 'mouse' || !this.#inMenu(event.target) || this.#inMenu(event.relatedTarget)) return;
    window.clearTimeout(this.#openTimer);
    if (!this.#openedByHover) return;
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      if (this.#openedByHover) this.#closeMega();
    }, 250);
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (this.#isOpen && !this.#inMenu(event.target)) this.#closeMega();
  };

  /* ---- Drawer drill-down -------------------------------------------------- */

  /** Fill one drawer tab's brand list, once. @param {Element} tab */
  async #buildTab(tab) {
    if (!(tab instanceof HTMLElement) || tab.dataset.fpBuilt) return;
    // The menu data arrives over the network, so wait for it before claiming
    // the tab as built -- otherwise a fast tap renders an empty panel and the
    // `fpBuilt` flag stops it ever being filled.
    const catalog = tab.getAttribute('data-catalog') || '';
    const index = await loadNavIndex(this.dataset.index || '');
    if (tab.dataset.fpBuilt) return;
    const brands = [...(index[catalog]?.brands || [])].sort((a, b) =>
      a.brand.localeCompare(b.brand, 'en-GB', { sensitivity: 'base' })
    );
    if (!brands.length) return;
    tab.dataset.fpBuilt = '1';

    const panel = tab.querySelector('.fp-catalog-nav__panel');
    if (!(panel instanceof HTMLElement)) return;

    const collectionUrl = tab.querySelector('.fp-catalog-nav__trigger')?.getAttribute('href') || '';

    const brandList = document.createElement('ul');
    brandList.className = 'fp-catalog-nav__brands';
    brandList.setAttribute('role', 'list');

    // The tab itself opens this list, so the collection page gets its own link.
    if (collectionUrl) {
      const allItem = document.createElement('li');
      const allLink = document.createElement('a');
      allLink.className = 'fp-catalog-nav__link fp-catalog-nav__link--all';
      allLink.href = collectionUrl;
      allLink.textContent = `All ${(tab.dataset.label || '').toLowerCase()}`.trim();
      allItem.append(allLink);
      brandList.append(allItem);
    }

    brands.forEach((entry) => {
      const brandItem = document.createElement('li');
      brandItem.className = 'fp-catalog-nav__brand';
      brandItem.dataset.brand = entry.brand;

      const brandLink = document.createElement('a');
      brandLink.className = 'fp-catalog-nav__link';
      brandLink.href = this.#brandHref(collectionUrl, entry.brand);
      brandLink.textContent = entry.brand;
      brandLink.setAttribute('aria-haspopup', 'true');
      brandLink.append(this.#chevron());
      brandItem.append(brandLink);

      const modelList = document.createElement('ul');
      modelList.className = 'fp-catalog-nav__models';
      modelList.dataset.brand = entry.brand;
      modelList.setAttribute('role', 'list');
      modelList.prepend(this.#backButton('All brands'));

      // Models come from the index already in natural order, newest
      // generation first within a name (scripts/build_filter_index.py).
      (entry.models || []).forEach((model) => {
        const modelItem = document.createElement('li');
        modelItem.className = 'fp-catalog-nav__model';
        const covers = this.#coversFor(catalog, model);

        if (covers.length > 1) {
          const modelButton = document.createElement('span');
          modelButton.className = 'fp-catalog-nav__link fp-catalog-nav__link--parent';
          modelButton.textContent = model.l;
          modelButton.append(this.#chevron());
          modelItem.append(modelButton);

          const styleList = document.createElement('ul');
          styleList.className = 'fp-catalog-nav__styles';
          styleList.setAttribute('role', 'list');
          styleList.prepend(this.#backButton(model.l));
          covers.forEach((cover) => {
            styleList.append(this.#styleLink(this.#labelForCover(cover), model, cover));
          });
          modelItem.append(styleList);
        } else if (covers.length) {
          const modelLink = document.createElement('a');
          modelLink.className = 'fp-catalog-nav__link';
          modelLink.href = this.#productHref(model, covers[0]);
          modelLink.dataset.model = model.l;
          modelLink.textContent = model.l;
          modelItem.append(modelLink);
        }

        modelList.append(modelItem);
      });

      brandItem.append(modelList);
      brandList.append(brandItem);
    });

    panel.replaceChildren(brandList);
    panel.removeAttribute('hidden');
  }

  /**
   * @param {string} label
   * @param {NavModel} model
   * @param {string} cover
   */
  #styleLink(label, model, cover) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.className = 'fp-catalog-nav__link';
    link.href = this.#productHref(model, cover);
    link.dataset.model = model.l;
    link.textContent = label;
    item.append(link);
    return item;
  }

  /**
   * The covers this model has products for, in menu order.
   * @param {string} catalog
   * @param {NavModel} model
   * @returns {string[]}
   */
  #coversFor(catalog, model) {
    const covers = COVER_ORDER.filter((cover) => model.c[cover]);
    // A van's driver cover also fits a single passenger seat, so a van sold
    // with a driver and a double passenger cover gets a "Single passenger
    // cover" link to the driver one.
    if (catalog === 'vans' && model.c.driver && model.c['double-passenger'] && !model.c.passenger) {
      covers.splice(covers.indexOf('driver') + 1, 0, 'single-passenger');
    }
    return covers;
  }

  /** @param {string} cover */
  #labelForCover(cover) {
    if (cover === 'double-passenger') return 'Double passenger cover';
    if (cover === 'single-passenger') return 'Single passenger cover';
    if (cover === 'passenger') return 'Passenger cover';
    if (cover === 'driver') return 'Driver cover';
    if (cover === 'low') return 'Low-back cover';
    return 'High-back cover';
  }

  /** @param {string} value */
  #handleize(value) {
    return String(value)
      .normalize('NFKD')
      .replace(/[^\x00-\x7F]/g, '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/\+/g, 'plus')
      .replace(/\//g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * The exact product for one model's cover.
   * @param {NavModel} model
   * @param {string} cover
   */
  #productHref(model, cover) {
    // The single passenger cover is the driver product, marked so
    // assets/fp-companion-seat.js labels the line "Seat: Single passenger".
    if (cover === 'single-passenger') return `/products/${model.c.driver}?seat=single-passenger`;
    return `/products/${model.c[cover]}`;
  }

  /**
   * A brand on its own stays on the collection, filtered by the brand's tag.
   * @param {string} collectionUrl
   * @param {string} brand
   */
  #brandHref(collectionUrl, brand) {
    if (!collectionUrl) return '#';
    const next = new URL(collectionUrl, window.location.origin);
    if (next.pathname.includes('/collections/')) {
      return `${next.pathname.replace(/\/$/, '')}/${this.#handleize(brand)}`;
    }
    return next.pathname;
  }

  #chevron() {
    const wrap = document.createElement('span');
    wrap.className = 'fp-catalog-nav__arrow';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 20 20" fill="none"><path d="M7.5 4.5 13 10l-5.5 5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return wrap;
  }

  /** @param {string} label */
  #backButton(label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'fp-catalog-nav__back';
    button.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M14 8H3M7 4 3 8l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const text = document.createElement('span');
    text.textContent = label;
    button.append(text);
    return button;
  }

  /** @param {Element} target The element a drawer click landed on. */
  #ensureBuilt(target) {
    const tab = target.closest('.fp-catalog-nav__tab.is-mega');
    if (tab) this.#buildTab(tab);
  }

  #syncDrawerLevel() {
    if (this.dataset.mode !== 'drawer') return;
    const tab = this.querySelector('.fp-catalog-nav__tab.is-open');
    const brand = tab?.querySelector('.fp-catalog-nav__brand.is-open');
    const model = brand?.querySelector('.fp-catalog-nav__model.is-open');
    this.classList.toggle('is-level-models', Boolean(brand) && !model);
    this.classList.toggle('is-level-fits', Boolean(model));
  }

  /** @param {MouseEvent} event */
  #onDrawerClick = (event) => {
    if (this.dataset.mode !== 'drawer') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    this.#ensureBuilt(target);

    const back = target.closest('.fp-catalog-nav__back');
    if (back instanceof HTMLElement) {
      event.preventDefault();
      const openModel = back.closest('.fp-catalog-nav__model.is-open');
      if (openModel instanceof HTMLElement) {
        openModel.classList.remove('is-open');
        this.#syncDrawerLevel();
        return;
      }
      const openBrand = back.closest('.fp-catalog-nav__brand.is-open');
      if (openBrand instanceof HTMLElement) {
        openBrand.classList.remove('is-open');
        this.#syncDrawerLevel();
        return;
      }
      const openTab = back.closest('.fp-catalog-nav__tab.is-open');
      if (openTab instanceof HTMLElement) {
        this.#setExpanded(openTab, false);
        this.#syncDrawerLevel();
      }
      return;
    }

    const brandLink = target.closest('.fp-catalog-nav__brand > .fp-catalog-nav__link');
    if (brandLink instanceof HTMLElement && brandLink.hasAttribute('aria-haspopup')) {
      event.preventDefault();
      const brand = brandLink.parentElement;
      if (!(brand instanceof HTMLElement)) return;
      brand.closest('.fp-catalog-nav__panel')
        ?.querySelectorAll('.fp-catalog-nav__brand.is-open')
        .forEach((item) => {
          if (item !== brand) item.classList.remove('is-open');
        });
      brand.classList.add('is-open');
      this.#syncDrawerLevel();
      return;
    }

    const modelParent = target.closest('.fp-catalog-nav__link--parent');
    if (modelParent instanceof HTMLElement) {
      event.preventDefault();
      const model = modelParent.parentElement;
      if (!(model instanceof HTMLElement)) return;
      model.closest('.fp-catalog-nav__models')
        ?.querySelectorAll('.fp-catalog-nav__model.is-open')
        .forEach((item) => {
          if (item !== model) item.classList.remove('is-open');
        });
      model.classList.add('is-open');
      this.#syncDrawerLevel();
      return;
    }

    const trigger = target.closest('.fp-catalog-nav__tab.is-mega > .fp-catalog-nav__trigger');
    if (trigger instanceof HTMLElement) {
      event.preventDefault();
      const tab = trigger.parentElement;
      if (!(tab instanceof HTMLElement)) return;
      const wasOpen = tab.classList.contains('is-open');
      this.querySelectorAll('.fp-catalog-nav__tab.is-open').forEach((openTab) => {
        openTab.querySelectorAll('.is-open').forEach((node) => node.classList.remove('is-open'));
        this.#setExpanded(openTab, false);
      });
      if (!wasOpen) this.#setExpanded(tab, true);
      this.#syncDrawerLevel();
    }
  };

  /** @param {Element} tab @param {boolean} open */
  #setExpanded(tab, open) {
    if (open) this.#buildTab(tab);
    tab.classList.toggle('is-open', open);
    const trigger = tab.querySelector('.fp-catalog-nav__trigger');
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    const panel = tab.querySelector(':scope > .fp-catalog-nav__panel');
    if (panel instanceof HTMLElement) {
      if (open) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    }
  }
}

customElements.define('fp-catalog-nav', FpCatalogNav);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest('.fp-catalog-nav__link[href], .fp-mega__link[href]');
  if (!(link instanceof HTMLAnchorElement)) return;
  try {
    const url = new URL(link.href, window.location.origin);
    let brand = url.searchParams.get('brand');
    let model = url.searchParams.get('model') || '';
    const catalog = link.closest('[data-catalog]')?.getAttribute('data-catalog') || '';
    if (!brand) {
      const brandEl = link.closest('[data-brand]');
      brand = brandEl instanceof HTMLElement ? brandEl.dataset.brand || '' : '';
      model = link.dataset.model || '';
    }
    if (!brand) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ brand, model, catalog }));
  } catch {
    /* ignore */
  }
});
